import React, { useState } from 'react';
import { Calendar, Clock, User, AlertCircle, Trash2, RotateCcw } from 'lucide-react';
import RecurrenceDeleteModal from './RecurrenceDeleteModal';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import { useTheme } from '../contexts/ThemeContext';

const ClientEditAppointmentModal = ({ appointment, employees, onSave, onClose, onDelete, canEditAppointments = true }) => {
  const { theme } = useTheme();
  const isDarkMode = document.documentElement.classList.contains('dark');

  const [appointmentData, setAppointmentData] = useState(() => {
    const date = appointment.date instanceof Date && !isNaN(appointment.date)
      ? appointment.date
      : appointment.date?.toDate ? appointment.date.toDate() : new Date();
    return {
      ...appointment,
      date: date,
      // Assurer que les valeurs de récurrence ont des défauts
      recurrenceFrequency: appointment.recurrenceFrequency || 1,
      recurrenceEndAfter: appointment.recurrenceEndAfter || 1,
    };
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showRecurrenceDeleteModal, setShowRecurrenceDeleteModal] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAppointmentData(prev => {
      const newData = { ...prev };
      if (name === 'date') {
        const newDate = new Date(value);
        newData[name] = isNaN(newDate.getTime()) ? prev.date : newDate;
      } else if (name === 'employeeId') {
        newData[name] = value === 'unassigned' ? null : value;
        newData.status = value === 'unassigned' ? 'libre' : 'assigne';
      } else if (name === 'recurrenceFrequency' || name === 'recurrenceEndAfter') {
        // Assurer que les valeurs numériques de récurrence sont au minimum 1
        newData[name] = Math.max(1, parseInt(value, 10) || 1);
      } else {
        newData[name] = type === 'checkbox' ? checked : value;
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(appointmentData);
      onClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du rendez-vous:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    // Si le rendez-vous est récurrent, afficher le modal de choix
    if (appointmentData.isRecurring && appointmentData.recurrenceSeriesId) {
      setShowRecurrenceDeleteModal(true);
    } else {
      // Sinon, utiliser la confirmation standard
      if (window.confirm("Êtes-vous sûr de vouloir supprimer ce rendez-vous ?")) {
        await performDelete('this');
      }
    }
  };

  const handleRecurrenceDeleteConfirm = async (scope) => {
    setShowRecurrenceDeleteModal(false);
    await performDelete(scope);
  };

  const performDelete = async (scope) => {
    setIsDeleting(true);
    try {
      await onDelete(appointmentData.id, scope);
      onClose();
    } catch (error) {
      console.error("Erreur lors de la suppression du rendez-vous:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getDayOfWeekName = (day) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[day];
  };

  const getWeekOfMonthName = (week) => {
    const weeks = ['premier', 'deuxième', 'troisième', 'quatrième', 'dernier'];
    return weeks[Math.min(week - 1, 4)];
  };

  const handleDaySelection = (day) => {
    setAppointmentData(prev => ({
      ...prev,
      recurrenceDay: prev.recurrenceDay?.includes(day)
        ? prev.recurrenceDay.filter(d => d !== day)
        : [...(prev.recurrenceDay || []), day]
    }));
  };

  return (
    <EnhancedModal isOpen={true} onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>
        Modifier le rendez-vous
      </ModalHeader>

      <ModalBody>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section Informations de base */}
          <div className="space-y-4">
            <h3 className={`text-lg font-semibold border-b pb-2 ${isDarkMode ? 'text-gray-100 border-gray-700' : 'text-gray-900 border-gray-200'}`}>
              Informations du rendez-vous
            </h3>

            {/* Date */}
            <div className="space-y-2">
              <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <Calendar className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} size={16} />
                Date du rendez-vous
              </label>
              <input
                type="date"
                name="date"
                value={appointmentData.date instanceof Date && !isNaN(appointmentData.date)
                  ? appointmentData.date.toISOString().split('T')[0]
                  : ''}
                onChange={handleChange}
                disabled={!canEditAppointments}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:cursor-not-allowed ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 disabled:bg-gray-800'
                    : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
                }`}
              />
            </div>

            {/* Horaires */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Clock className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} size={16} />
                  Heure de début
                </label>
                <input
                  type="time"
                  name="startTime"
                  value={appointmentData.startTime}
                  onChange={handleChange}
                  disabled={!canEditAppointments}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:cursor-not-allowed ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 disabled:bg-gray-800'
                      : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
                  }`}
                />
              </div>

              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Clock className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} size={16} />
                  Heure de fin
                </label>
                <input
                  type="time"
                  name="endTime"
                  value={appointmentData.endTime}
                  onChange={handleChange}
                  disabled={!canEditAppointments}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:cursor-not-allowed ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 disabled:bg-gray-800'
                      : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
                  }`}
                />
              </div>
            </div>

            {/* Intervenant */}
            <div className="space-y-2">
              <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <User className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} size={16} />
                Intervenant assigné
              </label>
              <select
                name="employeeId"
                value={appointmentData.employeeId || 'unassigned'}
                onChange={handleChange}
                disabled={!canEditAppointments}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:cursor-not-allowed ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 disabled:bg-gray-800'
                    : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
                }`}
              >
                <option value="unassigned">Non assigné</option>
                {employees.map(emp => (
                  <option key={emp.userId} value={emp.userId}>
                    {emp.name || `${emp.firstName} ${emp.lastName}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Section Récurrence Toggle */}
          <div className="space-y-4">
            <div className={`flex items-center space-x-3 p-4 rounded-lg border ${
              isDarkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className={`p-2 rounded-full ${
                appointmentData.isRecurring
                  ? isDarkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-600'
                  : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
              }`}>
                <RotateCcw size={20} />
              </div>
              <div className="flex-1">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="isRecurring"
                    checked={appointmentData.isRecurring}
                    onChange={handleChange}
                    disabled={!canEditAppointments}
                    className="sr-only"
                  />
                  <div className={`w-10 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                    appointmentData.isRecurring ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                  } ${!canEditAppointments ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <div className={`bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out transform ${
                      appointmentData.isRecurring ? 'translate-x-6' : 'translate-x-1'
                    } mt-1`} />
                  </div>
                  <span className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    Rendez-vous récurrent
                  </span>
                </label>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Planifier ce rendez-vous de manière répétée selon une fréquence définie
                </p>
              </div>
            </div>
          </div>

          {appointmentData.isRecurring && (
            <div className={`space-y-4 p-4 rounded-lg border ${
              isDarkMode
                ? 'bg-blue-900/20 border-blue-700'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <h4 className={`font-medium flex items-center ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                <RotateCcw className="mr-2" size={16} />
                Paramètres de récurrence {!canEditAppointments && <span className={`text-sm font-normal ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>(lecture seule)</span>}
              </h4>
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Fréquence</label>
                <select
                  name="recurrenceBase"
                  value={appointmentData.recurrenceBase}
                  onChange={handleChange}
                  disabled={!canEditAppointments}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 disabled:bg-gray-800'
                      : 'bg-white border-blue-300 text-gray-900 disabled:bg-gray-100'
                  }`}
                >
                  <option value="daily">Journalière</option>
                  <option value="weekly">Hebdomadaire</option>
                  <option value="monthly">Mensuelle</option>
                </select>
              </div>
              {appointmentData.recurrenceBase === 'daily' && (
                <div className={`flex items-center space-x-3 p-3 rounded-lg border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                }`}>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tous les</span>
                  <input
                    type="number"
                    name="recurrenceFrequency"
                    value={appointmentData.recurrenceFrequency || 1}
                    onChange={handleChange}
                    min="1"
                    disabled={!canEditAppointments}
                    className={`w-20 p-2 border rounded text-center focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
                      isDarkMode
                        ? 'bg-gray-600 border-gray-500 text-gray-100 disabled:bg-gray-800'
                        : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
                    }`}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>jour(s)</span>
                </div>
              )}

              {appointmentData.recurrenceBase === 'weekly' && (
                <>
                  <div className={`flex items-center space-x-3 p-3 rounded-lg border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tous les</span>
                    <input
                      type="number"
                      name="recurrenceFrequency"
                      value={appointmentData.recurrenceFrequency}
                      onChange={handleChange}
                      min="1"
                      disabled={!canEditAppointments}
                      className={`w-20 p-2 border rounded text-center focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
                        isDarkMode
                          ? 'bg-gray-600 border-gray-500 text-gray-100 disabled:bg-gray-800'
                          : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
                      }`}
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>semaine(s)</span>
                  </div>
                  <div className="space-y-2">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Jours de la semaine</p>
                    <div className="flex flex-wrap gap-2">
                      {[0, 1, 2, 3, 4, 5, 6].map(day => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleDaySelection(day)}
                          disabled={!canEditAppointments}
                          className={`px-3 py-2 rounded text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            appointmentData.recurrenceDay?.includes(day)
                              ? isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                              : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {getDayOfWeekName(day).substring(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {appointmentData.recurrenceBase === 'monthly' && (
                <>
                  <div>
                    <select
                      name="recurrenceMonthType"
                      value={appointmentData.recurrenceMonthType}
                      onChange={handleChange}
                      disabled={!canEditAppointments}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-gray-100 disabled:bg-gray-800'
                          : 'bg-white border-blue-300 text-gray-900 disabled:bg-gray-100'
                      }`}
                    >
                      <option value="dayOfMonth">Le même jour du mois</option>
                      <option value="dayOfWeek">Le même jour de la semaine</option>
                    </select>
                  </div>
                  {appointmentData.recurrenceMonthType === 'dayOfMonth' ? (
                    <div className={`text-sm p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                    }`}>
                      Répéter le {appointmentData.recurrenceMonthDay} de chaque mois
                    </div>
                  ) : (
                    <div className={`text-sm p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                    }`}>
                      Répéter le {getWeekOfMonthName(appointmentData.recurrenceMonthWeek)} {getDayOfWeekName(appointmentData.recurrenceMonthWeekday)} de chaque mois
                    </div>
                  )}
                  <div className={`flex items-center space-x-3 p-3 rounded-lg border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tous les</span>
                    <input
                      type="number"
                      name="recurrenceFrequency"
                      value={appointmentData.recurrenceFrequency}
                      onChange={handleChange}
                      min="1"
                      disabled={!canEditAppointments}
                      className={`w-20 p-2 border rounded text-center focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
                        isDarkMode
                          ? 'bg-gray-600 border-gray-500 text-gray-100 disabled:bg-gray-800'
                          : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
                      }`}
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>mois</span>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Fin de la récurrence</label>
                <select
                  name="recurrenceEndType"
                  value={appointmentData.recurrenceEndType}
                  onChange={handleChange}
                  disabled={!canEditAppointments}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100 disabled:bg-gray-800'
                      : 'bg-white border-blue-300 text-gray-900 disabled:bg-gray-100'
                  }`}
                >
                  <option value="never">Jamais</option>
                  <option value="after">Après un nombre d'occurrences</option>
                  <option value="on">À une date spécifique</option>
                </select>
              </div>
              {appointmentData.recurrenceEndType === 'after' && (
                <div className={`flex items-center space-x-3 p-3 rounded-lg border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                }`}>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Après</span>
                  <input
                    type="number"
                    name="recurrenceEndAfter"
                    value={appointmentData.recurrenceEndAfter}
                    onChange={handleChange}
                    min="1"
                    disabled={!canEditAppointments}
                    className={`w-20 p-2 border rounded text-center focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed ${
                      isDarkMode
                        ? 'bg-gray-600 border-gray-500 text-gray-100 disabled:bg-gray-800'
                        : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
                    }`}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>occurrences</span>
                </div>
              )}
              {appointmentData.recurrenceEndType === 'on' && (
                <div className="space-y-2">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Date de fin</label>
                  <input
                    type="date"
                    name="recurrenceEndDate"
                    value={appointmentData.recurrenceEndDate || ''}
                    onChange={handleChange}
                    disabled={!canEditAppointments}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100 disabled:bg-gray-800'
                        : 'bg-white border-blue-300 text-gray-900 disabled:bg-gray-100'
                    }`}
                  />
                </div>
              )}
            </div>
          )}
        </form>
      </ModalBody>

      <ModalFooter>
        <div className="flex justify-between w-full">
          <div>
            {canEditAppointments && (
              <ModalButton
                variant="danger"
                onClick={handleDelete}
                disabled={isDeleting || isSaving}
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </ModalButton>
            )}
          </div>
          <div className="flex space-x-2">
            <ModalButton
              variant="secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Annuler
            </ModalButton>
            {canEditAppointments && (
              <ModalButton
                variant="primary"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? 'Mise à jour...' : 'Mettre à jour'}
              </ModalButton>
            )}
          </div>
        </div>
      </ModalFooter>

      <RecurrenceDeleteModal
        isOpen={showRecurrenceDeleteModal}
        onClose={() => setShowRecurrenceDeleteModal(false)}
        onConfirm={handleRecurrenceDeleteConfirm}
        appointment={appointmentData}
      />
    </EnhancedModal>
  );
};

export default ClientEditAppointmentModal;
